<script setup>
import { ref, watch, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterSelect from '../../socialMonitoring/components/FilterSelect.vue';
import { EVENT_TYPES, EVENT_STATUSES } from '../utils/eventMeta';

const props = defineProps({
  open: { type: Boolean, default: false },
  event: { type: Object, default: null },
  scripts: { type: Array, default: () => [] },
  saving: { type: Boolean, default: false },
  defaultDate: { type: String, default: '' },
});
const emit = defineEmits(['close', 'save', 'delete']);

const form = ref({
  title: '',
  eventType: 'agenda_publica',
  startsAt: '',
  endsAt: '',
  location: '',
  address: '',
  script: '',
  expectedAudience: '',
  status: 'agendado',
});
const scriptPicker = ref('');

const isEdit = computed(() => !!props.event?.id);
const typeOptions = EVENT_TYPES.map(e => ({
  value: e.value,
  label: e.label,
  icon: e.icon,
}));
const statusOptions = EVENT_STATUSES.map(s => ({
  value: s.value,
  label: s.label,
}));
const scriptOptions = computed(() => [
  { value: '', label: 'Nenhum — escrever livre' },
  ...props.scripts
    .filter(s => !form.value.eventType || s.eventType === form.value.eventType)
    .map(s => ({
      value: String(s.id),
      label: s.title,
      hint: `${s.durationMinutes || '?'} min${s.isGlobal ? ' · template' : ''}`,
    })),
]);

watch(
  () => [props.open, props.event, props.defaultDate],
  () => {
    if (!props.open) return;
    if (props.event) {
      form.value = {
        title: props.event.title,
        eventType: props.event.eventType,
        startsAt: props.event.startsAt ? props.event.startsAt.slice(0, 16) : '',
        endsAt: props.event.endsAt ? props.event.endsAt.slice(0, 16) : '',
        location: props.event.location || '',
        address: props.event.address || '',
        script: props.event.script || '',
        expectedAudience: props.event.expectedAudience || '',
        status: props.event.status,
      };
    } else {
      const d = props.defaultDate ? `${props.defaultDate}T10:00` : '';
      form.value = {
        title: '',
        eventType: 'agenda_publica',
        startsAt: d,
        endsAt: '',
        location: '',
        address: '',
        script: '',
        expectedAudience: '',
        status: 'agendado',
      };
    }
    scriptPicker.value = '';
  },
  { immediate: true }
);

function importScript() {
  const s = props.scripts.find(x => String(x.id) === scriptPicker.value);
  if (!s) return;
  form.value.script = s.body;
  if (!form.value.title) form.value.title = s.title;
}

function submit() {
  const payload = { ...form.value };
  if (payload.expectedAudience === '') delete payload.expectedAudience;
  if (!payload.endsAt) delete payload.endsAt;
  if (props.event?.id) payload.id = props.event.id;
  emit('save', payload);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="emit('close')"
    >
      <div
        class="absolute inset-0 bg-black/50 backdrop-blur-sm"
        @click="emit('close')"
      />
      <div
        class="relative z-10 flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-n-background shadow-2xl"
      >
        <div
          class="flex items-center justify-between border-b border-n-weak px-5 py-3"
        >
          <h3 class="text-base font-bold text-n-slate-12">
            {{ isEdit ? 'Editar evento' : 'Novo evento' }}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>

        <div class="flex-1 overflow-y-auto p-5">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="md:col-span-2">
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Título</label>
              <input
                v-model="form.title"
                type="text"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Tipo</label>
              <FilterSelect
                v-model="form.eventType"
                :options="typeOptions"
                min-width="100%"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Status</label>
              <FilterSelect
                v-model="form.status"
                :options="statusOptions"
                min-width="100%"
              />
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Início</label>
              <input
                v-model="form.startsAt"
                type="datetime-local"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Fim (opcional)</label>
              <input
                v-model="form.endsAt"
                type="datetime-local"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Local</label>
              <input
                v-model="form.location"
                type="text"
                placeholder="Ex.: Praça Central"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Endereço</label>
              <input
                v-model="form.address"
                type="text"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>

            <div class="md:col-span-2">
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Público estimado</label>
              <input
                v-model.number="form.expectedAudience"
                type="number"
                min="0"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>

            <div class="md:col-span-2">
              <div class="mb-2 flex items-end gap-2">
                <div class="flex-1">
                  <label
                    class="mb-1 block text-xs font-semibold text-n-slate-11"
                    >Importar script pronto</label>
                  <FilterSelect
                    v-model="scriptPicker"
                    :options="scriptOptions"
                    min-width="100%"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  icon="i-lucide-copy"
                  :disabled="!scriptPicker"
                  @click="importScript"
                >
                  Usar
                </Button>
              </div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Roteiro / script do evento</label>
              <textarea
                v-model="form.script"
                rows="10"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 font-mono text-xs"
              />
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-between border-t border-n-weak px-5 py-3"
        >
          <Button
            v-if="isEdit"
            variant="ghost"
            size="sm"
            icon="i-lucide-trash-2"
            color="ruby"
            @click="emit('delete', event)"
          >
            Excluir
          </Button>
          <span v-else />
          <div class="flex gap-2">
            <Button variant="outline" size="sm" @click="emit('close')">
              Cancelar
            </Button>
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :is-loading="saving"
              :disabled="!form.title.trim() || !form.startsAt"
              @click="submit"
            >
              {{ isEdit ? 'Salvar' : 'Criar' }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
